import curry from 'ramda/src/curry';

import formatPrice from '/lib/utils/formatPrice';
import DiscountCodeModal from '/components/layout/DiscountCodeModal';
import Heading from '/components/ui/Heading';
import Link from '/components/ui/Link';
import Paragraph from '/components/ui/Paragraph';
import Divider from '/components/ui/Divider';
import ProjectType, {
    isProjectResource,
    ProjectStage,
} from '/types/projectType';
import OfferType from '/types/offerType';
import DraftType from '/types/draftType';
import ReservationType from '/types/ReservationType';
import { LineItems, DiscountCodeLineItem } from '/types/lineItemTypes';
import ResourceType from '/types/resourceType';
import CartType from '/types/cartType';
import EnovaCashbackSummary from '/components/layout/EnovaCashbackSummary';
import PriceComponent from './PriceComponent';
import ExtraCosts from './ExtraCosts';
import PriceType from '/types/priceType';
import { useMemo } from 'react';

const matchType = curry((type, item) => item.type === type);

const sum = (acc: number, item: number) => acc + Math.abs(item) || 0;

interface Props {
    jobToatlCost?: PriceType;
    resource?: ProjectType | DraftType | OfferType | CartType;
    resourceType?:
        | ResourceType.OFFER
        | ResourceType.PROJECT
        | ResourceType.CART;
    onDiscountCodeApplied?: () => void;
    withDiscountCode?: boolean;
    withTax?: boolean;
    withByline?: boolean;
    label?: string;
    className?: string;
    reservation?: ReservationType; // reservation might be passed as prop when we are not dealing with the cart or existing resource
    priceComponentType?: 'gross' | 'net';
    withEnovaInfo?: boolean;
}

export default function Price({
    jobToatlCost,
    label = 'Fastpris',
    withByline = true,
    resource,
    resourceType,
    onDiscountCodeApplied,
    withDiscountCode = false,
    withTax = true,
    className,
    reservation,
    priceComponentType = 'gross',
    withEnovaInfo,
}: Props) {
    const priceComponents = (() => {
        const lineItems: LineItems = (resource?.lineItems || []) as LineItems;

        return {
            subtotal:
                resource?.totals?.totalBeforeDiscount?.[priceComponentType] ||
                0,
            discountFromCode: lineItems
                .filter(matchType('DISCOUNT_CODE'))
                .map((li) => {
                    const lineItem = li as DiscountCodeLineItem;
                    return (
                        lineItem.total?.[priceComponentType] || lineItem.amount
                    );
                })
                .reduce(sum, 0),
            discountFromAlterations: lineItems
                .filter(matchType('ALTERATION'))
                .map((li) => li.total?.[priceComponentType])
                .reduce(sum, 0),
            systemDiscount: lineItems
                .filter(matchType('SYSTEM_DISCOUNT'))
                .map((li) => li.total?.[priceComponentType])
                .reduce(sum, 0),
        };
    })();

    // We need to check if the resource even has the property stage
    const isSolarUpFrontPayment =
        (resource as ProjectType)?.stage === ProjectStage.UP_FRONT_PAYMENT;

    const total = (() => {
        if (isSolarUpFrontPayment) {
            return { net: 2500, gross: 2500, tax: 0 };
        }
        if (jobToatlCost) return jobToatlCost;
        if (resource?.totals?.total) return resource.totals.total;
        return { gross: 0, net: 0, tax: 0 };
    })();

    const shouldShowPriceComponents = !!(
        !jobToatlCost &&
        priceComponents.subtotal &&
        priceComponents.subtotal !== total[priceComponentType]
    );

    if (!total) return null;

    return (
        <div className={['price', className].filter((c) => c).join(' ')}>
            {shouldShowPriceComponents && (
                <>
                    <PriceComponent
                        className="total-before-discount"
                        label="Delsum"
                        value={priceComponents.subtotal}
                    />
                    <ExtraCosts
                        resource={resource}
                        reservation={reservation}
                        extraCostType={priceComponentType}
                    />
                    <PriceComponent
                        className="system-discount"
                        label="Kvantumsrabatt"
                        value={priceComponents.systemDiscount}
                        isDiscount={true}
                    />
                    <PriceComponent
                        className="price-alteration"
                        label="Prisavslag"
                        value={priceComponents.discountFromAlterations}
                        isDiscount={true}
                    />
                    <PriceComponent
                        className="discount-code"
                        label="Rabattkode"
                        value={priceComponents.discountFromCode}
                        isDiscount={true}
                    />
                    <Divider className="mt-48 mb-48" />
                </>
            )}

            {withDiscountCode && (
                <>
                    <DiscountCodeModal
                        resource={resource}
                        resourceType={resourceType}
                        onCodeApplied={onDiscountCodeApplied || null}
                    />
                    {!priceComponents.discountFromCode && (
                        <Divider className="mt-48 mb-48" />
                    )}
                </>
            )}

            {priceComponentType === 'net' && (
                <>
                    <Heading
                        medium
                        tag="div"
                        className="flex wrap justify-space-between total-price"
                    >
                        <span>Totalt inkl. mva</span>
                        <span className="total-formatted">
                            {formatPrice(total.gross)}
                        </span>
                    </Heading>

                    {!isSolarUpFrontPayment && (
                        <PriceComponent
                            label="Totalt eks. mva"
                            value={total.net}
                        />
                    )}

                    {withTax && (
                        <PriceComponent
                            className="tax-info"
                            label="Mva 25%"
                            value={total.tax}
                        />
                    )}
                </>
            )}

            {priceComponentType === 'gross' && (
                <>
                    <Heading
                        large
                        tag="div"
                        className="flex wrap justify-space-between total-price"
                    >
                        <span>{label}</span>
                        <span className="total-formatted">
                            {formatPrice(total?.gross)}
                        </span>
                    </Heading>

                    {withTax && (
                        <PriceComponent
                            className="tax-info"
                            label="Herav MVA"
                            value={total.tax}
                        />
                    )}
                </>
            )}
            {resource && withEnovaInfo && (
                <EnovaCashbackSummary
                    lineItems={resource.lineItems || []}
                    total={total}
                    className="mt-24"
                />
            )}

            {withByline && (
                <Paragraph medium className="pt-48">
                    <Link purple medium to="/fastpris">
                        Fastprisen
                    </Link>{' '}
                    inkluderer alt og vår <br />
                    bransjeledende SpotOnGaranti™
                </Paragraph>
            )}
        </div>
    );
}
